import {fuzzyMatchHighlight} from '@wandb/weave/common/util/fuzzyMatch';
import {isEmpty} from 'lodash';
import React, {
  memo,
  ReactElement,
  ReactFragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {PanelBankSearchHistoryItem} from 'src/components/PanelBank/types';

import {isInJupyterNotebook} from '../../setup';
import {useDebounceState} from '../../util/hooks';

const PanelSearchInputContext = React.createContext<{
  searchQuery: string; // original query typed by user (not debounced)
  searchHistory: PanelBankSearchHistoryItem[];
  searchInputCallbackRef(node: HTMLInputElement | null): void;
  onChange(val: string): void;
  onAddToHistory(query: string): void;
}>({
  searchQuery: '',
  searchInputCallbackRef: () => {},
  onChange: () => {},
  onAddToHistory: (query: string) => {},
  searchHistory: [],
});
PanelSearchInputContext.displayName = 'PanelSearchInputContext';

const PanelSearchDebouncedContext = React.createContext<{
  isSearching: boolean;
  debouncedSearchQuery: string;
  makeHighlightSearchTitle(title: string): ReactFragment;
}>({
  isSearching: false,
  debouncedSearchQuery: '',
  makeHighlightSearchTitle: () => <></>,
});
PanelSearchDebouncedContext.displayName = 'PanelSearchDebouncedContext';

type Props = {
  children: ReactElement;
  savedSearch?: string;
  searchHistory: PanelBankSearchHistoryItem[];
  onDebouncedSearch?: (newSearch: string) => void;
  onAddToHistory(query: string): void;
};

const PanelSearchContextProviderComp: React.FC<Props> = ({
  children,
  savedSearch,
  onDebouncedSearch,
  onAddToHistory,
  searchHistory,
}) => {
  // Focus the search input if we're not in a jupyter notebook
  const searchInputCallbackRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (!isInJupyterNotebook() && node != null) {
        node.focus();
      }
    },
    []
  );

  const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebounceState(
    // restore the user's saved search
    savedSearch ?? '',
    100
  );

  useEffect(() => {
    // don't save if identical
    if (savedSearch === debouncedSearchQuery) {
      return;
    }
    // don't force save an empty string if there's no current saved search
    if (savedSearch == null && debouncedSearchQuery === '') {
      return;
    }
    onDebouncedSearch?.(debouncedSearchQuery);
  }, [debouncedSearchQuery, onDebouncedSearch, savedSearch]);

  const onChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery]
  );

  // This takes in a string (e.g. a section title, or panel title)
  // And returns jsx to display the string with the search match letters highlighted
  const makeHighlightSearchTitle = useCallback(
    (title: string) =>
      fuzzyMatchHighlight(
        title,
        debouncedSearchQuery // TODO: regex support?
      ),
    [debouncedSearchQuery]
  );

  const inputState = useMemo(
    () => ({
      searchInputCallbackRef,
      searchQuery, // original query typed by user (not debounced). used for display.
      onChange,
      onAddToHistory,
      searchHistory,
    }),
    [
      searchInputCallbackRef,
      searchQuery,
      onChange,
      onAddToHistory,
      searchHistory,
    ]
  );

  const debouncedState = useMemo(
    () => ({
      debouncedSearchQuery: debouncedSearchQuery.trim(), // changes a lot, maybe should have its own context
      isSearching: !isEmpty(debouncedSearchQuery.trim()),
      makeHighlightSearchTitle,
    }),
    [debouncedSearchQuery, makeHighlightSearchTitle]
  );

  return (
    <PanelSearchInputContext.Provider value={inputState}>
      <PanelSearchDebouncedContext.Provider value={debouncedState}>
        {children}
      </PanelSearchDebouncedContext.Provider>
    </PanelSearchInputContext.Provider>
  );
};

export const PanelSearchContextProvider = memo(PanelSearchContextProviderComp);

export function usePanelSearchDebouncedContext() {
  return useContext(PanelSearchDebouncedContext);
}

export function usePanelSearchInputContext() {
  return useContext(PanelSearchInputContext);
}
